// src/pages/income/env/disinfection/InvoiceForm.jsx 청구서 메뉴


import React, { useEffect, useState } from "react";
import styles from "./IncomeDraftCreate.module.css";
import api from "../../../../api";

export default function InvoiceForm() {
  // ✅ 상태값들
  const [category] = useState("환경/소독");
  const [subcategory, setSubcategory] = useState("소독방역");
  const [title, setTitle] = useState("");
  const [draftDate, setDraftDate] = useState(() => toISO(new Date()));
  const [claimDate, setClaimDate] = useState("");
  const [workMonth, setWorkMonth] = useState(""); // ✅ 작업월 추가
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");

  // ✅ 거래처 상태
  const [vendors, setVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState("");
  const [vendorId, setVendorId] = useState(null);
  const [showVendorList, setShowVendorList] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  // ✅ 거래방법
  const [payMethod, setPayMethod] = useState("계좌이체");
  const payMethods = ["계좌이체", "카드결제", "현금결제"];
  const PAY_METHOD_TO_CODE = { "계좌이체": "transfer", "카드결제": "card", "현금결제": "cash" };

  // ✅ 거래처 목록 불러오기
  useEffect(() => {
    async function fetchVendors() {
      setLoadingVendors(true);
      try {
        const res = await api.get("/vendors/");
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setVendors(data);
      } catch (err) {
        console.error("❌ 거래처 목록 불러오기 실패:", err);
      } finally {
        setLoadingVendors(false);
      }
    }
    fetchVendors();
  }, []);

  // ✅ 거래처 선택
  const handlePickVendor = (v) => {
    setVendorId(v.id);
    setVendorInput(v.name);
    setShowVendorList(false);
    setHighlightIndex(-1);
  };

  // ✅ 거래처 입력 시 필터링
  const handleChangeVendor = (e) => {
    setVendorInput(e.target.value);
    setVendorId(null);
    setShowVendorList(true);
    setHighlightIndex(-1);
  };

  // ✅ 방향키로 이동 시 하이라이트만 변경 (입력값은 유지)
  useEffect(() => {
    if (highlightIndex >= 0) {
      setShowVendorList(true);
    }
  }, [highlightIndex]);
  

  // ✅ 키보드 조작 (⬆⬇ Enter)
  const handleVendorKeyDown = (e) => {
    if (!showVendorList || filteredVendors.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredVendors.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredVendors.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredVendors[highlightIndex]) {
        handlePickVendor(filteredVendors[highlightIndex]);
      }
    }
  };

  // ✅ 입력값 기준 거래처 필터링
  const filteredVendors = vendors.filter((v) =>
    v.name.toLowerCase().includes(vendorInput.toLowerCase())
  );

  // ✅ 자동완성 제목 생성 (작업월 > 청구일 > 기본)
  useEffect(() => {
    if ((workMonth || claimDate) && vendorInput && subcategory) {
      const baseDate = workMonth
        ? new Date(workMonth + "-01")
        : new Date(claimDate);
      const year = baseDate.getFullYear();
      const month = String(baseDate.getMonth() + 1).padStart(2, "0");
      setTitle(`${year}년 ${month}월 ${vendorInput} ${subcategory} 청구서`);
    }
  }, [workMonth, claimDate, vendorInput, subcategory]);

  // ✅ DB 저장 기능 (IncomeDraftCreate와 동일한 방식)
  const handleSave = async () => {
    if (!draftDate || !claimDate || !vendorInput || !amount) {
      alert("모든 필드를 입력해 주세요.");
      return;
    }

    const payload = {
      category,
      subcategory,
      title,
      draft_date: draftDate,
      claim_date: claimDate,
      work_month: workMonth,
      vendor_id: vendorId,
      vendor_name: vendorInput,
      amount: Number(amount),
      pay_method: PAY_METHOD_TO_CODE[payMethod] || "transfer",
      desc,
    };

    try {
      const res = await api.post("/income-drafts/", payload);
      console.log("✅ 청구서 저장 성공:", res.data);
      alert(`청구서 저장 완료 ✅\n거래처: ${vendorInput}\n금액: ${amount}원`);
    } catch (err) {
      console.error("❌ 청구서 저장 실패:", err.response || err);
      alert("서버에 청구서를 저장하지 못했습니다. 관리자에게 문의하세요.");
    }
  };

  return (
    <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
      <h3 style={{ marginBottom: "10px" }}>청구서 작성</h3>

      <Row label="제목(자동완성)">
        <input
          value={title}
          readOnly
          placeholder="청구일·작업월·거래처 입력 시 자동 생성됩니다."
        />
      </Row>

      <Row label="사업구분"><input value={category} readOnly /></Row>

      <Row label="세부구분">
        <input
          value={subcategory}
          onChange={(e) => setSubcategory(e.target.value)}
          placeholder="예: 소독방역"
        />
      </Row>

      <Row label="작성일">
        <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} />
      </Row>

      <Row label="청구일">
        <input type="date" value={claimDate} onChange={(e) => setClaimDate(e.target.value)} />
      </Row>

      <Row label="작업월(귀속월)">
        <input type="month" value={workMonth} onChange={(e) => setWorkMonth(e.target.value)} />
      </Row>

      <Row label="거래처">
        <div className={styles.partnerRow}>
          <input
            placeholder="직접 입력 또는 검색"
            value={vendorInput}
            onChange={handleChangeVendor}
            onKeyDown={handleVendorKeyDown}
          />
          <button
            type="button"
            className={styles.partnerButton}
            onClick={() => setShowVendorList((v) => !v)}
          >
            ▼
          </button>
        </div>
        {showVendorList && (
          <div className={styles.partnerBox}>
            {loadingVendors ? (
              <div>불러오는 중...</div>
            ) : filteredVendors.length > 0 ? (
              filteredVendors.map((v, i) => (
                <div
                  key={v.id}
                  className={`${styles.partnerItem} ${
                    i === highlightIndex ? styles.activeItem : ""
                  }`}
                  onClick={() => handlePickVendor(v)}
                >
                  {v.name}
                </div>
              ))
            ) : (
              <div>일치하는 거래처가 없습니다.</div>
            )}
          </div>
        )}
      </Row>

      <Row label="금액">
        <input
          inputMode="numeric"
          placeholder="숫자만"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))}
        />
      </Row>

      <Row label="거래방법">
        <div className={styles.inline}>
          {payMethods.map((method) => (
            <label key={method} className={styles.check}>
              <input
                type="checkbox"
                checked={payMethod === method}
                onChange={() => setPayMethod(method)}
              />
              {method}
            </label>
          ))}
        </div>
      </Row>

      <Row label="본문/비고">
        <textarea
          rows={4}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="청구 관련 메모를 입력하세요."
        />
      </Row>

      <div className={styles.actions}>
        <button type="button" onClick={handleSave} className={styles.primaryBtn}>저장</button>
      </div>
    </form>
  );
}

// ✅ 공통 Row 컴포넌트
function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>{label}</div>
      <div className={styles.rowField}>{children}</div>
    </div>
  );
}

// ✅ 날짜 포맷 함수
function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
